import type { LoadResult } from "@cuekards/core/script-source";
import type {
  OverlayScrollState,
  OverlayViewState,
  CuekardsApi,
  ScriptLoadedPayload,
} from "@cuekards/core/ipc";
import type {
  CaptureFormat,
  CaptureKind,
  DeviceState,
} from "@cuekards/core/media-devices";
import type {
  CaptureCommand,
  CaptureEvent,
  RecordingStatus,
} from "@cuekards/core/recording";
import type { RecoveryState } from "@cuekards/core/recovery";
import type { ScrollMode } from "@cuekards/core/scroll-mode";
import type { FollowerState } from "@cuekards/core/speech";
import type { VoiceCommandState } from "@cuekards/core/voice-commands";
import type {
  OverlayDefaults,
  OverlaySettingsState,
  StepDirection,
} from "@cuekards/core/overlay-defaults";
import type { NudgeDirection } from "@cuekards/core/script-model";
import type { ShortcutCommand, ShortcutState } from "@cuekards/core/shortcuts";
import type { TakeMarkerQuality } from "@cuekards/core/sidecar";
import { IpcChannel } from "@cuekards/core/ipc";
import type { ContractClient } from "./contract-client.js";

/**
 * `window.cuekards`, built over a port instead of over a preload script.
 *
 * A deliberate transliteration of the desktop app's `preload/index.ts`, method
 * for method and channel for channel. Nothing is added, nothing is dropped, and
 * nothing is renamed — which is what lets every component in `@cuekards/ui` mount
 * in a tab with no change at all.
 *
 * Channels the web has no answer for are still here, and still call through:
 * the output folder and its reveal action have no meaning in a browser, but the
 * contract is a shape both hosts share, and a host that quietly narrows it is a
 * host the shared components can drift away from. An unimplemented channel is a
 * request the core refuses, which is a visible failure rather than a silent one.
 */
export interface CuekardsApiPorts {
  /**
   * Where a forked microphone frame goes (FR-31, web ticket 14).
   *
   * The one method here whose destination is the host's to choose, because it is
   * the one whose destination is not the core worker: the recogniser has a worker
   * of its own, and the frames go to it directly. Everything else on this contract
   * is a question the core answers or a command it acts on; this is audio on its
   * way past.
   *
   * Omitted, it travels the contract like everything else and is dropped by a core
   * worker that does not handle the channel. That is the right default rather than
   * a broken one: it is what a page with no recognition worker attached does, and
   * it matches what the desktop's preload does with the same call.
   */
  sendSpeechFrame?: (samples: Float32Array) => void;
}

export function createCuekardsApi(
  client: ContractClient,
  ports: CuekardsApiPorts = {},
): CuekardsApi {
  return {
    loadScript: (url: string): Promise<LoadResult> =>
      client.request(IpcChannel.loadScript, url),

    getScript: (): Promise<ScriptLoadedPayload | null> =>
      client.request(IpcChannel.getScript),

    getLastDocUrl: (): Promise<string | undefined> =>
      client.request(IpcChannel.getLastDocUrl),

    clearScript: (): Promise<void> => client.request(IpcChannel.clearScript),

    onScriptLoaded: (
      listener: (payload: ScriptLoadedPayload) => void,
    ): (() => void) => client.subscribe(IpcChannel.scriptLoaded, listener),

    getPosition: (): Promise<number> => client.request(IpcChannel.getPosition),

    onPositionChanged: (listener: (position: number) => void): (() => void) =>
      client.subscribe(IpcChannel.positionChanged, listener),

    getOverlayView: (): Promise<OverlayViewState> =>
      client.request(IpcChannel.getOverlayView),

    onOverlayViewChanged: (
      listener: (state: OverlayViewState) => void,
    ): (() => void) => client.subscribe(IpcChannel.overlayViewChanged, listener),

    getScrollState: (): Promise<OverlayScrollState> =>
      client.request(IpcChannel.getScrollState),

    onScrollStateChanged: (
      listener: (state: OverlayScrollState) => void,
    ): (() => void) => client.subscribe(IpcChannel.scrollStateChanged, listener),

    refreshScript: (): Promise<LoadResult | null> =>
      client.request(IpcChannel.refreshScript),

    getRecordingStatus: (): Promise<RecordingStatus> =>
      client.request(IpcChannel.getRecordingStatus),

    onRecordingStatusChanged: (
      listener: (status: RecordingStatus) => void,
    ): (() => void) =>
      client.subscribe(IpcChannel.recordingStatusChanged, listener),

    startRecording: (): void => {
      client.send(IpcChannel.startRecording);
    },

    togglePauseRecording: (): void => {
      client.send(IpcChannel.togglePauseRecording);
    },

    stopRecording: (): void => {
      client.send(IpcChannel.stopRecording);
    },

    onCaptureCommand: (
      listener: (command: CaptureCommand) => void,
    ): (() => void) => client.subscribe(IpcChannel.captureCommand, listener),

    sendCaptureEvent: (event: CaptureEvent): void => {
      client.send(IpcChannel.captureEvent, event);
    },

    reportDevices: (
      devices: { deviceId: string; kind: string; label: string }[],
    ): void => {
      client.send(IpcChannel.reportDevices, devices);
    },

    getDeviceState: (): Promise<DeviceState> =>
      client.request(IpcChannel.getDeviceState),

    onDeviceStateChanged: (
      listener: (state: DeviceState) => void,
    ): (() => void) => client.subscribe(IpcChannel.deviceStateChanged, listener),

    selectDevice: (kind: CaptureKind, deviceId: string | null): void => {
      client.send(IpcChannel.selectDevice, kind, deviceId);
    },

    setCaptureFormat: (format: CaptureFormat | null): void => {
      client.send(IpcChannel.setCaptureFormat, format);
    },

    setDeviceOff: (kind: CaptureKind, off: boolean): void => {
      client.send(IpcChannel.setDeviceOff, kind, off);
    },

    getRecoveryState: (): Promise<RecoveryState> =>
      client.request(IpcChannel.getRecoveryState),

    onRecoveryStateChanged: (
      listener: (state: RecoveryState) => void,
    ): (() => void) =>
      client.subscribe(IpcChannel.recoveryStateChanged, listener),

    recoverSession: (id: string): void => {
      client.send(IpcChannel.recoverSession, id);
    },

    dismissSession: (id: string): void => {
      client.send(IpcChannel.dismissSession, id);
    },

    discardSession: (id: string): void => {
      client.send(IpcChannel.discardSession, id);
    },

    sendSpeechFrame: (samples: Float32Array): void => {
      const direct = ports.sendSpeechFrame;
      if (direct !== undefined) direct(samples);
      else client.send(IpcChannel.speechFrame, samples);
    },

    getFollowerState: (): Promise<FollowerState> =>
      client.request(IpcChannel.getFollowerState),

    onFollowerStateChanged: (
      listener: (state: FollowerState) => void,
    ): (() => void) =>
      client.subscribe(IpcChannel.followerStateChanged, listener),

    toggleRehearsal: (): void => {
      client.send(IpcChannel.toggleRehearsal);
    },

    toggleCrawl: (): void => {
      client.send(IpcChannel.toggleCrawl);
    },

    resyncFollower: (): void => {
      client.send(IpcChannel.resyncFollower);
    },

    getVoiceCommandState: (): Promise<VoiceCommandState> =>
      client.request(IpcChannel.getVoiceCommandState),

    onVoiceCommandStateChanged: (
      listener: (state: VoiceCommandState) => void,
    ): (() => void) =>
      client.subscribe(IpcChannel.voiceCommandStateChanged, listener),

    setVoiceCommandsEnabled: (enabled: boolean): void => {
      client.send(IpcChannel.setVoiceCommandsEnabled, enabled);
    },

    getOverlaySettings: (): Promise<OverlaySettingsState> =>
      client.request(IpcChannel.getOverlaySettings),

    onOverlaySettingsChanged: (
      listener: (state: OverlaySettingsState) => void,
    ): (() => void) =>
      client.subscribe(IpcChannel.overlaySettingsChanged, listener),

    setOverlayDefaults: (defaults: OverlayDefaults): void => {
      client.send(IpcChannel.setOverlayDefaults, defaults);
    },

    setConstantSpeedWpm: (wpm: number): void => {
      client.send(IpcChannel.setConstantSpeedWpm, wpm);
    },

    saveOverlayAsDefaults: (): void => {
      client.send(IpcChannel.saveOverlayAsDefaults);
    },

    resetOverlaySettings: (): void => {
      client.send(IpcChannel.resetOverlaySettings);
    },

    getOutputDirectory: (): Promise<string> =>
      client.request(IpcChannel.getOutputDirectory),

    revealOutputDirectory: (): void => {
      client.send(IpcChannel.revealOutputDirectory);
    },

    revealLastTake: (): void => {
      client.send(IpcChannel.revealLastTake);
    },

    resetDevices: (): void => {
      client.send(IpcChannel.resetDevices);
    },

    nudgePosition: (direction: NudgeDirection): void => {
      client.send(IpcChannel.nudgePosition, direction);
    },

    restartPosition: (): void => {
      client.send(IpcChannel.restartPosition);
    },

    cycleScrollMode: (): void => {
      client.send(IpcChannel.cycleScrollMode);
    },

    setScrollMode: (mode: ScrollMode): void => {
      client.send(IpcChannel.setScrollMode, mode);
    },

    cycleTextAlign: (): void => {
      client.send(IpcChannel.cycleTextAlign);
    },

    /* Answered by the phone's host alone: a tab has no sideways prompter. */
    cycleSideAlign: (): void => {
      client.send(IpcChannel.cycleSideAlign);
    },

    stepFontSize: (direction: StepDirection): void => {
      client.send(IpcChannel.stepFontSize, direction);
    },

    // Neither of these has a handler in the core worker, and neither will: a tab
    // is not a window over other applications and has no file on a disk to write
    // a marker beside. They are here because the contract is one shape both
    // hosts implement, and an unhandled channel is a refusal that names itself.
    stepOpacity: (direction: StepDirection): void => {
      client.send(IpcChannel.stepOpacity, direction);
    },

    markTake: (quality: TakeMarkerQuality): void => {
      client.send(IpcChannel.markTake, quality);
    },

    getShortcutState: (): Promise<ShortcutState> =>
      client.request(IpcChannel.getShortcutState),

    onShortcutStateChanged: (
      listener: (state: ShortcutState) => void,
    ): (() => void) => client.subscribe(IpcChannel.shortcutStateChanged, listener),

    setShortcutBinding: (command: ShortcutCommand, chord: string): void => {
      client.send(IpcChannel.setShortcutBinding, command, chord);
    },

    resetShortcuts: (): void => {
      client.send(IpcChannel.resetShortcuts);
    },
  };
}
